import type { Design } from "../neon/Design";
import type { NeonTube } from "../neon/NeonTube";
import type { ToolManager } from "../tools/ToolManager";
import { BACKDROPS, type BackdropId } from "../app/SceneSetup";
import { TEMPLATES } from "../neon/templates";
import {
  DEFAULT_COLOR,
  DEFAULT_INTENSITY,
  DEFAULT_LIGHTING,
  DEFAULT_THICKNESS,
  NEON_PALETTE,
  type LightingSettings,
} from "../neon/types";
import { ImportDialog, type ImportPayload } from "./ImportDialog";

export interface UIHooks {
  onShare(): void;
  onClear(): void;
  onNameChange(name: string): void;

  onUndo(): void;
  onRedo(): void;
  onExport(): void;
  onLoadTemplate(id: string): void;
  onBackdropChange(id: BackdropId): void;
  onLightingChange(lighting: LightingSettings): void;
  onLightingReset(): void;

  /** Inspector edits — apply to all currently selected tubes. */
  onApplyColor(hex: string): void;
  onApplyThickness(t: number): void;
  onApplyIntensity(i: number): void;
  onApplyWarm(warm: boolean): void;

  /** Image-import finished — add the traced contours as tubes. */
  onImportContours(payload: ImportPayload): void;
  /** Fires whenever the import dialog closes (cancel, import, or Esc). */
  onImportDialogClosed(): void;
}

export interface StatusInfo {
  widthIn: number;
  heightIn: number;
  watts: number;
  monthlyCost: number;
}

/**
 * Thin DOM layer. Knows nothing about Three.js — it surfaces brush state
 * and metric readouts, and forwards user actions through hooks. Tools read
 * the current brush values via UI#getBrushColor/Thickness/Intensity.
 */
export class UI {
  private brushColor = DEFAULT_COLOR;
  private brushThickness = DEFAULT_THICKNESS;
  private brushIntensity = DEFAULT_INTENSITY;
  private brushWarm = false;
  private selection: NeonTube[] = [];

  private el: Record<string, HTMLElement> = {};
  private importDialog!: ImportDialog;

  constructor(
    private readonly design: Design,
    private readonly tools: ToolManager,
    private readonly hooks: UIHooks,
  ) {
    this.bindElements();
    this.buildToolbar();
    this.buildSwatches();
    this.buildTemplateSelect();
    this.buildBackdropSelect();
    this.wireControls();
    this.renderHints();
    this.importDialog = new ImportDialog(
      document.getElementById("import-dialog") as HTMLDialogElement,
      {
        onImport: (p) => this.hooks.onImportContours(p),
        onClose: () => this.hooks.onImportDialogClosed(),
      },
    );

    design.subscribe(() => this.refresh());
    tools.subscribe(() => this.refresh());
    this.refresh();
  }

  openImportDialog(): void {
    this.importDialog.open(this.brushColor);
  }

  setSelection(tubes: NeonTube[]): void {
    this.selection = tubes;
    const primary = tubes[0];
    if (primary) {
      this.brushColor = `#${primary.color.getHexString()}`;
      (this.el.customColor as HTMLInputElement).value = this.brushColor;
      this.brushThickness = primary.thickness;
      (this.el.thickness as HTMLInputElement).value = String(primary.thickness);
      this.brushIntensity = primary.intensity;
      (this.el.intensity as HTMLInputElement).value = String(primary.intensity);
      this.brushWarm = primary.warm;
      (this.el.warm as HTMLInputElement).checked = primary.warm;
      this.refreshSwatches();
    }
  }

  setUndoRedoState(canUndo: boolean, canRedo: boolean): void {
    (this.el.undo as HTMLButtonElement).disabled = !canUndo;
    (this.el.redo as HTMLButtonElement).disabled = !canRedo;
  }

  setBackdrop(id: BackdropId): void {
    (this.el.backdropSelect as HTMLSelectElement).value = id;
  }

  setLighting(lighting: LightingSettings): void {
    (this.el.lightAmbient as HTMLInputElement).value = String(lighting.ambient);
    (this.el.lightRim as HTMLInputElement).value = String(lighting.rim);
  }

  private readLighting(): LightingSettings {
    return {
      ambient: parseFloat((this.el.lightAmbient as HTMLInputElement).value),
      rim: parseFloat((this.el.lightRim as HTMLInputElement).value),
    };
  }

  setStatus(info: StatusInfo | null): void {
    if (!info) {
      this.el.statusSize.textContent = "—";
      this.el.statusPower.textContent = "—";
      return;
    }
    this.el.statusSize.textContent = `${Math.round(info.widthIn)}″ × ${Math.round(info.heightIn)}″`;
    this.el.statusPower.textContent = `${info.watts.toFixed(0)}W · ~$${info.monthlyCost.toFixed(2)}/mo`;
  }

  getBrushColor(): string {
    return this.brushColor;
  }
  getBrushThickness(): number {
    return this.brushThickness;
  }
  getBrushIntensity(): number {
    return this.brushIntensity;
  }
  getBrushWarm(): boolean {
    return this.brushWarm;
  }

  setName(name: string): void {
    (this.el.name as HTMLInputElement).value = name;
  }

  setHint(msg: string): void {
    this.el.statusHint.textContent = msg;
  }

  toast(msg: string): void {
    this.el.toast.textContent = msg;
    this.el.toast.classList.add("visible");
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.el.toast.classList.remove("visible");
    }, 1800);
  }
  private toastTimer = 0;

  private bindElements(): void {
    const get = (id: string) => document.getElementById(id)!;
    this.el.toolbar = get("toolbar");
    this.el.swatches = get("swatches");
    this.el.customColor = get("custom-color");
    this.el.thickness = get("thickness");
    this.el.intensity = get("intensity");
    this.el.warm = get("warm");
    this.el.share = get("btn-share");
    this.el.clear = get("btn-clear");
    this.el.undo = get("btn-undo");
    this.el.redo = get("btn-redo");
    this.el.export = get("btn-export");
    this.el.templateSelect = get("template-select");
    this.el.backdropSelect = get("backdrop-select");
    this.el.lightAmbient = get("light-ambient");
    this.el.lightRim = get("light-rim");
    this.el.lightReset = get("btn-light-reset");
    this.el.name = get("design-name");
    this.el.statusTool = get("status-tool");
    this.el.statusTubes = get("status-tubes");
    this.el.statusSize = get("status-size");
    this.el.statusPower = get("status-power");
    this.el.statusHint = get("status-hint");
    this.el.toast = get("toast");
    this.el.hintList = get("hint-list");
  }

  private buildToolbar(): void {
    this.el.toolbar.innerHTML = "";
    for (const tool of this.tools.listTools()) {
      const btn = document.createElement("button");
      btn.className = "tool-btn";
      btn.title = `${tool.name} — ${tool.hint}`;
      btn.innerHTML = tool.icon;
      btn.dataset.toolId = tool.id;
      btn.addEventListener("click", () => this.tools.activate(tool.id));
      this.el.toolbar.appendChild(btn);
    }
  }

  private buildSwatches(): void {
    this.el.swatches.innerHTML = "";
    for (const entry of NEON_PALETTE) {
      const s = document.createElement("div");
      s.className = "swatch";
      s.style.background = entry.hex;
      s.style.color = entry.hex;
      s.dataset.color = entry.hex;
      s.title = entry.name;
      s.addEventListener("click", () => this.setBrushColor(entry.hex));
      this.el.swatches.appendChild(s);
    }
    this.refreshSwatches();
  }

  private buildTemplateSelect(): void {
    const sel = this.el.templateSelect as HTMLSelectElement;
    for (const t of TEMPLATES) {
      const opt = document.createElement("option");
      opt.value = t.id;
      opt.textContent = t.label;
      sel.appendChild(opt);
    }
  }

  private buildBackdropSelect(): void {
    const sel = this.el.backdropSelect as HTMLSelectElement;
    for (const b of BACKDROPS) {
      const opt = document.createElement("option");
      opt.value = b.id;
      opt.textContent = b.label;
      sel.appendChild(opt);
    }
  }

  private refreshSwatches(): void {
    for (const s of this.el.swatches.querySelectorAll(".swatch")) {
      (s as HTMLElement).classList.toggle(
        "active",
        (s as HTMLElement).dataset.color?.toLowerCase() === this.brushColor.toLowerCase(),
      );
    }
  }

  private setBrushColor(hex: string): void {
    this.brushColor = hex;
    (this.el.customColor as HTMLInputElement).value = hex;
    this.refreshSwatches();
    if (this.selection.length) this.hooks.onApplyColor(hex);
  }

  private wireControls(): void {
    (this.el.customColor as HTMLInputElement).addEventListener("input", (e) => {
      this.setBrushColor((e.target as HTMLInputElement).value);
    });
    (this.el.thickness as HTMLInputElement).addEventListener("input", (e) => {
      this.brushThickness = parseFloat((e.target as HTMLInputElement).value);
      if (this.selection.length) this.hooks.onApplyThickness(this.brushThickness);
    });
    (this.el.intensity as HTMLInputElement).addEventListener("input", (e) => {
      this.brushIntensity = parseFloat((e.target as HTMLInputElement).value);
      if (this.selection.length) this.hooks.onApplyIntensity(this.brushIntensity);
    });
    (this.el.warm as HTMLInputElement).addEventListener("change", (e) => {
      this.brushWarm = (e.target as HTMLInputElement).checked;
      if (this.selection.length) this.hooks.onApplyWarm(this.brushWarm);
    });
    this.el.share.addEventListener("click", () => this.hooks.onShare());
    this.el.clear.addEventListener("click", () => {
      if (this.design.tubeCount === 0) return;
      if (!confirm("Clear all tubes?")) return;
      this.hooks.onClear();
    });
    this.el.undo.addEventListener("click", () => this.hooks.onUndo());
    this.el.redo.addEventListener("click", () => this.hooks.onRedo());
    this.el.export.addEventListener("click", () => this.hooks.onExport());
    (this.el.templateSelect as HTMLSelectElement).addEventListener("change", (e) => {
      const id = (e.target as HTMLSelectElement).value;
      if (!id) return;
      this.hooks.onLoadTemplate(id);
      (e.target as HTMLSelectElement).value = "";
    });
    (this.el.backdropSelect as HTMLSelectElement).addEventListener("change", (e) => {
      this.hooks.onBackdropChange((e.target as HTMLSelectElement).value as BackdropId);
    });
    const onLight = () => this.hooks.onLightingChange(this.readLighting());
    (this.el.lightAmbient as HTMLInputElement).addEventListener("input", onLight);
    (this.el.lightRim as HTMLInputElement).addEventListener("input", onLight);
    this.el.lightReset.addEventListener("click", () => {
      this.setLighting(DEFAULT_LIGHTING);
      this.hooks.onLightingReset();
    });
    (this.el.name as HTMLInputElement).addEventListener("input", (e) => {
      this.hooks.onNameChange((e.target as HTMLInputElement).value);
    });
  }

  private renderHints(): void {
    const hints = [
      "<code>D</code> Draw • <code>B</code> Bend • <code>V</code> Select",
      "<code>P</code> Paint • <code>X</code> Erase • <code>O</code> Orbit",
      "<code>I</code> Import image • Shift-click for multi-select",
      "<code>⌘Z</code> undo • <code>⌘⇧Z</code> redo • <code>⌘D</code> duplicate",
      "<code>⌘E</code> export PNG • <code>Delete</code> remove selection",
    ];
    this.el.hintList.innerHTML = hints.map((h) => `<li>${h}</li>`).join("");
  }

  private refresh(): void {
    const active = this.tools.getActive();
    this.el.statusTool.textContent = active?.name ?? "—";
    const count = this.design.tubeCount;
    this.el.statusTubes.textContent = `${count} tube${count === 1 ? "" : "s"}`;
    for (const btn of this.el.toolbar.querySelectorAll<HTMLButtonElement>(".tool-btn")) {
      btn.classList.toggle("active", btn.dataset.toolId === active?.id);
    }
  }
}
